import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  rosterResponseSchema,
  unitResponseSchema,
  type CreateUnitRequest,
  type Unit,
  type UpdateUnitRequest,
} from '@unit/shared';
import { apiPatch, apiPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { useApi } from '../../lib/useApi.js';
import { ActionError } from '../../components/ActionError.js';
import { PersonName } from '../../components/PersonName.js';

const input =
  'rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none';
const primary =
  'rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40';
const secondary =
  'rounded-lg px-3 py-1.5 text-sm text-slate-600 ring-1 ring-slate-300 transition hover:bg-slate-100 disabled:opacity-40';

/**
 * "Add a platoon" at the root, or "add a team" under one platoon
 * (REQUIREMENTS §2.6, org structure editor). Admin only — the page only
 * renders these for an admin, and the endpoint refuses anyone else.
 */
export function AddUnitForm({
  parent,
  onCreated,
}: {
  /** null: create a platoon at the root. A platoon: create a team under it. */
  parent: Unit | null;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const create = useAction((body: CreateUnitRequest) =>
    apiPost('/units', unitResponseSchema, body),
  );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const result = await create.run({
      type: parent ? 'team' : 'platoon',
      name,
      parentId: parent?.id ?? null,
      commanderUids: [],
    });
    if (result) {
      setName('');
      setOpen(false);
      onCreated();
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={parent ? `add-team-${parent.id}` : 'add-platoon'}
        className="text-sm text-slate-500 underline-offset-4 hover:underline"
      >
        {parent ? t('org.addTeam') : t('org.addPlatoon')}
      </button>
    );
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="flex flex-wrap items-center gap-2">
      <input
        className={input}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={parent ? t('org.teamNamePlaceholder') : t('org.platoonNamePlaceholder')}
        aria-label={t('org.unitName')}
        data-testid="new-unit-name"
        autoFocus
      />
      <button type="submit" disabled={create.busy || name.trim().length === 0} className={primary}>
        {t('org.create')}
      </button>
      <button type="button" onClick={() => setOpen(false)} className={secondary}>
        {t('org.cancel')}
      </button>
      <div className="basis-full">
        <ActionError error={create.error} />
      </div>
    </form>
  );
}

/**
 * Rename, (de)activate, and choose commanders for one unit.
 *
 * Commanders are picked from the unit's own roster (a team) or from everyone
 * in the platoon (a platoon): a commander who is not in the unit they command
 * would be the org chart lying about itself.
 */
export function UnitAdminRow({ unit, onChanged }: { unit: Unit; onChanged: () => void }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  // The roster is only fetched once the editor opens — the tree page would
  // otherwise make one call per unit before anyone clicks anything.
  const roster = useApi(editing ? `/units/${unit.id}/soldiers` : null, rosterResponseSchema);
  const candidates = roster.kind === 'ready' ? roster.data.soldiers : [];
  const [name, setName] = useState(unit.name);
  const [commanders, setCommanders] = useState<string[]>(unit.commanderUids);
  const update = useAction((body: UpdateUnitRequest) =>
    apiPatch(`/units/${unit.id}`, unitResponseSchema, body),
  );

  const save = async () => {
    const body: UpdateUnitRequest = {};
    if (name.trim() !== unit.name) body.name = name.trim();
    if (commanders.join() !== unit.commanderUids.join()) body.commanderUids = commanders;
    if (Object.keys(body).length === 0) {
      setEditing(false);
      return;
    }
    if (await update.run(body)) {
      setEditing(false);
      onChanged();
    }
  };

  const toggleActive = async () => {
    if (await update.run({ active: !unit.active })) onChanged();
  };

  if (!editing) {
    return (
      <span className="flex items-center gap-2 text-xs">
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid={`edit-unit-${unit.id}`}
          className="text-slate-500 underline-offset-4 hover:underline"
        >
          {t('org.edit')}
        </button>
        <button
          type="button"
          onClick={() => void toggleActive()}
          disabled={update.busy}
          data-testid={`toggle-unit-${unit.id}`}
          className="text-slate-500 underline-offset-4 hover:underline disabled:opacity-40"
        >
          {unit.active ? t('org.deactivate') : t('org.activate')}
        </button>
        <ActionError error={update.error} />
      </span>
    );
  }

  return (
    <div className="mt-2 flex flex-col gap-2 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200">
      <input
        className={input}
        value={name}
        onChange={(e) => setName(e.target.value)}
        aria-label={t('org.unitName')}
        data-testid={`unit-name-input-${unit.id}`}
      />
      <fieldset>
        <legend className="text-xs text-slate-500">{t('org.commanders')}</legend>
        <p className="text-xs text-slate-400">{t('org.commandersHint')}</p>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {roster.kind === 'loading' && (
            <span className="text-xs text-slate-400">{t('home.loading')}</span>
          )}
          {roster.kind === 'ready' && candidates.length === 0 && (
            <span className="text-xs text-slate-400">{t('org.noCandidates')}</span>
          )}
          {candidates.map((c) => {
            const on = commanders.includes(c.uid);
            return (
              <label
                key={c.uid}
                className={`cursor-pointer rounded-lg px-2.5 py-1 text-xs ring-1 ${
                  on ? 'bg-slate-900 text-white ring-slate-900' : 'text-slate-600 ring-slate-300'
                }`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={on}
                  onChange={() =>
                    setCommanders((cur) =>
                      on ? cur.filter((u) => u !== c.uid) : [...cur, c.uid],
                    )
                  }
                />
                <PersonName person={c} />
              </label>
            );
          })}
        </div>
      </fieldset>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void save()}
          disabled={update.busy}
          data-testid={`save-unit-${unit.id}`}
          className={primary}
        >
          {t('org.save')}
        </button>
        <button type="button" onClick={() => setEditing(false)} className={secondary}>
          {t('org.cancel')}
        </button>
      </div>
      <ActionError error={update.error} />
    </div>
  );
}
